import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from '../lib/toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Use our custom Resend-based password reset
      await axios.post(`${API_URL}/api/auth/custom/forgot-password`, {
        email: email.toLowerCase()
      });
      
      setSent(true);
      toast.success('Password reset email sent!');
    } catch (error) {
      // API always returns success message for security, so this is a real error
      const errorMessage = error.response?.data?.detail || 'Failed to send reset email';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--theme-bg)' }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center mb-8">
          <img 
            src="/logo-wantd-navy.png"
            alt="WANTD" 
            className="h-12 w-auto"
          />
        </Link>

        {/* Card */}
        <div className="bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-2xl p-8 shadow-card">
          {!sent ? (
            <>
              <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] text-center mb-2">
                Reset Password
              </h1>
              <p className="text-[var(--theme-text-secondary)] text-center mb-8">
                Enter your email and we'll send you a link to reset your password
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email */}
                <div>
                  <label className="form-label">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--theme-text-muted)]" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-10 bg-[var(--theme-bg)] border-[var(--theme-border)] text-[var(--theme-text-primary)] placeholder:text-[var(--theme-text-muted)]"
                      required
                      data-testid="reset-email-input"
                    />
                  </div>
                </div>

                {/* Submit */}
                <Button 
                  type="submit" 
                  className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white rounded-full h-12"
                  disabled={loading}
                  data-testid="reset-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">
                Check Your Email
              </h2>
              <p className="text-[var(--theme-text-secondary)] mb-6">
                We've sent a password reset link to<br />
                <strong className="text-[var(--theme-text-primary)]">{email}</strong>
              </p>
              <p className="text-sm text-[var(--theme-text-muted)] mb-6">
                Didn't receive the email? Check your spam folder or{' '}
                <button 
                  onClick={() => setSent(false)}
                  className="text-[#C9956C] hover:text-[#B8845B] font-medium"
                >
                  try again
                </button>
              </p>
            </div>
          )}

          {/* Back to Login */}
          <Link 
            to="/login" 
            className="flex items-center justify-center gap-2 mt-6 text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)]"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
